import { useState, useCallback } from "react"
import { professorRequest } from "services/requests"
import { getAccount } from "services/parentBridge"
import { isRunningFromIframe } from "common/functions"
import storage from "utils/persistentStorage"
import { LOCAL_STORAGE_PREFIX } from "common/constants"

const prefix = "account"

const getCommunicationsCacheKey = (userId) =>
  `${LOCAL_STORAGE_PREFIX}professor_communications_${userId}`

export const useProfessorAccountApi = () => {
  const [isFetching, setIsFetching] = useState(false)

  const getUser = async () => {
    setIsFetching(true)
    let user = null
    if (isRunningFromIframe()) {
      user = await getAccount()
    } else {
      const response = await professorRequest.sendRequest({
        endpoint: `${prefix}`,
        method: "get",
      })
      user = response.data
    }
    setIsFetching(false)
    return user
  }

  const changePassword = async (currentPassword, newPassword) => {
    setIsFetching(true)
    try {
      return await professorRequest.sendRequest({
        endpoint: `${prefix}/changePassword`,
        body: {
          currentPassword,
          newPassword,
        },
        method: "post",
      })
    } finally {
      setIsFetching(false)
    }
  }

  const editAccount = async (formData) => {
    setIsFetching(true)
    try {
      return await professorRequest.sendFormData({
        endpoint: `${prefix}/edit`,
        formData,
      })
    } finally {
      setIsFetching(false)
    }
  }

  const configurationCompleted = async () => {
    setIsFetching(true)
    try {
      return await professorRequest.sendRequest({
        endpoint: `${prefix}/configurationCompleted`,
        method: "post",
      })
    } finally {
      setIsFetching(false)
    }
  }

  const createAccount = async ({
    code,
    languageId,
    account,
    institution,
    schooling,
    ssoUser,
    recaptchaToken,
  }) => {
    setIsFetching(true)
    try {
      const body = {
        code,
        languageId,
        account,
        institution,
        schooling,
        ssoUser,
        recaptchaToken,
      }

      Object.keys(body).forEach(
        (key) => body[key] === undefined && delete body[key]
      )

      return await professorRequest.sendRequest({
        endpoint: `${prefix}/create`,
        body,
        method: "post",
      })
    } finally {
      setIsFetching(false)
    }
  }

  const updateInterfaceLanguage = async (languageId, otherOptions = {}) => {
    setIsFetching(true)
    try {
      return await professorRequest.sendRequest({
        endpoint: `${prefix}/interfaceLanguage`,
        body: { languageId },
        method: "put",
        otherOptions,
      })
    } finally {
      setIsFetching(false)
    }
  }

  const getCommunicationsOptions = useCallback(
    async ({ userId = 0, forceRefresh = false } = {}) => {
      if (!forceRefresh && userId > 0) {
        try {
          const cached = await storage.getItem(
            getCommunicationsCacheKey(userId)
          )
          if (cached) return cached
        } catch (storageError) {
          console.warn(
            "Failed to read communications options from cache",
            storageError
          )
        }
      }

      setIsFetching(true)
      try {
        const response = await professorRequest.sendRequest({
          endpoint: `${prefix}/communicationsOptions`,
          method: "get",
        })
        if (response?.data && userId > 0) {
          try {
            await storage.setItem(
              getCommunicationsCacheKey(userId),
              response.data
            )
          } catch (storageError) {
            console.warn(
              "Failed to write communications options to cache",
              storageError
            )
          }
        }
        return response?.data || null
      } catch (requestError) {
        console.error(
          "Failed to fetch communications options from API",
          requestError
        )
        return null
      } finally {
        setIsFetching(false)
      }
    },
    []
  )

  const updateCommunicationsOptions = useCallback(
    async (options, { userId = 0 } = {}) => {
      setIsFetching(true)
      try {
        const response = await professorRequest.sendRequest({
          endpoint: `${prefix}/communicationsOptions`,
          body: options,
          method: "put",
        })
        if (userId > 0) {
          try {
            await storage.setItem(getCommunicationsCacheKey(userId), {
              acceptDirectMessages: options.acceptDirectMessages,
              acceptGradeReview: options.acceptGradeReview,
            })
          } catch (storageError) {
            console.warn(
              "Failed to update communications options in cache",
              storageError
            )
          }
        }
        return response
      } finally {
        setIsFetching(false)
      }
    },
    []
  )

  return {
    isFetching,
    changePassword,
    editAccount,
    configurationCompleted,
    createAccount,
    updateInterfaceLanguage,
    getCommunicationsOptions,
    updateCommunicationsOptions,
    getUser,
  }
}

export default useProfessorAccountApi
